import { ruleValidation } from "../components/forms/customFunctions"
import { onGetDate } from "./formatTime"

/**
 * @module Utils/Forms
 * @description his file contains functions to handle forms
 * 
 */

/**
 * @function
 * @description The function checks if a form is valid based on specified keys or all required fields.
 * @param form - The form parameter is an object that represents a form. It contains properties that
 * represent form fields, such as input values, validation rules, and error messages.
 * @param keys - An array of strings representing the keys of the form object that need to be
 * validated. If this parameter is provided, only the specified keys will be checked for validity.
 * @returns The function `isFormValid` returns an array of strings representing the names of the form
 * fields that are not valid. These fields are determined based on the `form` object and the `keys`
 * array passed as arguments to the function. If `keys` is provided, the function checks only the
 * fields specified in `keys` and returns the names of the invalid fields. If `keys` is
 */
export const isFormValid = (form, keys) => {
    let errors = []
    if(form){
        if(keys){
            keys.forEach(item => {
                if(!form[item].isValid) errors.push(item)
            })
        }else{
            Object.keys(form).forEach(item => {
                if(form[item].isRequired && !form[item].isValid) errors.push(item)
            })
        }
    }
    return errors
} 

/**
 * @function
 * @description The function sets errors to a form by updating its properties.
 * @param form - The `form` parameter is an object that represents a form. It likely contains
 * properties for each input field in the form, such as `name`, `email`, `password`, etc. Each input
 * field property is itself an object that contains information about the field, such as its current
 * value, whether
 * @param errors - An array of strings representing the names of the form fields that have errors.
 * @returns The function `onSetErrorsToForm` returns a new object that is a copy of the `form` object
 * passed as an argument, with some of its properties modified based on the `errors` array also passed
 * as an argument. Specifically, for each item in the `errors` array, the function sets the `isVisited`
 * property of the corresponding property in the `temp` object to `
 */
export const onSetErrorsToForm = (form, errors) => {
    let temp = {...form}
    if(errors && errors.length) errors.forEach(item => {
        temp[item].isVisited = true
        temp[item].isValid = false
    })
    return temp
}

/**
 * @function
 * @description The function extracts valid form data based on specified keys or all keys.
 * @param form - an object representing a form, where each key is the name of a form field and the
 * value is an object containing information about that field (such as its value and whether it is
 * valid or not).
 * @param keys - An optional array of strings representing the keys of the form object that should be
 * included in the returned data. If this parameter is not provided, all keys with valid values in the
 * form object will be included.
 * @returns The function `onGetFormData` returns an object `temp` that contains key-value pairs of form
 * data. The keys are the names of the form fields and the values are the values entered by the user in
 * those fields. The function filters out any form fields that are not valid. If the `keys` parameter
 * is provided, the function only returns the form data for the specified keys. If `
 */
export const onGetFormData = (form, keys) => {
    let temp = {}
    if(form){
        if(keys){
            keys.forEach(item => {
                if(form[item].isValid) temp = {...temp, [item]:form[item].value}
            })
        }else{
            Object.keys(form).forEach(item => {
                if(form[item].isValid) temp = {...temp, [item]:form[item].value}
            })
        }
        
    }
    return temp
}

/**
 * @function
 * @description The function returns an object containing the valid and changed form data.
 * @param form - an object containing form data with keys as field names and values as objects with
 * properties "isValid" and "value"
 * @param origin - The `origin` parameter is likely a reference to the original data object that the
 * form is being compared against. It is used to determine if any changes have been made to the form
 * data.
 * @returns The function `onGetSelectedFormData` returns an object `temp` that contains the key-value
 * pairs of form fields that have valid values and are different from their original values.
 */
export const onGetSelectedFormData = (form, origin) => {
    let temp = {}
    if(form){
        Object.keys(form).forEach(item => {
            if(form[item].isValid && form[item].value !== origin[item]) temp = {...temp, [item]:form[item].value}
        })
    }
    return temp
}

/**
 * @function
 * @description The function initializes a form with values from an origin and translations.
 * @param form - The form object that contains the structure and configuration of the form fields.
 * @param origin - The `origin` parameter is an object that contains values for the form fields. These
 * values will be used to pre-populate the form fields when the form is initialized.
 * @param translations - The `translations` parameter is an object that contains translations for the
 * form fields. It is used to update the configuration of each form field with translated values.
 * @returns The function `onInitForm` returns a modified copy of the `form` object with updated values
 * and configurations based on the `origin` and `translations` parameters.
 */
export const onInitForm = (form, origin, translations) => {
    let response = {...form}
    Object.keys(form).forEach(item => {
        if(origin){
            const value = origin[item]
            if(value) response[item] = {...response[item], value:value, isVisited:true, isValid:true}
        }
        if(translations && translations[item]) response[item].config = {...response[item].config, ...translations[item]}
    })
    return response
}

/**
 * @function
 * @description The function initializes a form with values from an origin object and performs validation on the
 * values.
 * @param form - an object representing a form with input fields and their configurations
 * @param origin - The `origin` parameter is an object that contains the initial values for the form
 * fields.
 * @returns The function `onInitFormFromOrigin` returns a modified copy of the `form` object with
 * updated values and validation status based on the values and rules provided in the `origin` object.
 */
export const onInitFormFromOrigin = (form, origin) => {
    let response = {...form}
    Object.keys(origin).forEach(item => {
        let value = origin[item]
        const inputType = response[item]?.config?.type
        if(inputType === "date"){
            value = onGetDate(value)
        }
        const isValid  = ruleValidation(value, response[item].rules, null);
        if(value){
            
            response[item] = {
                ...response[item], 
                value:value, 
                isVisited:true, 
                isValid:isValid
            }
        }
    })
    return response
}